// apps/client/src/hooks/useClientSets.ts
import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../lib/firebase';

export interface ClientSet {
  id: string;
  clientSetName: string;
  customerNames: string[];
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
  loanCount: number;
  totalValueCents: number;
}

interface UseClientSetsParams {
  companyId: string;
  userId?: string;
  userRole?: string;
}

interface UseClientSetsReturn {
  clientSets: ClientSet[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch client sets for a company
 * RBAC:
 * - Brokers can only see their own client sets
 * - Admins can see all client sets in their company
 */
export function useClientSets({
  companyId,
  userId,
  userRole = 'broker',
}: UseClientSetsParams): UseClientSetsReturn {
  const [clientSets, setClientSets] = useState<ClientSet[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchClientSets = async () => {
    if (!companyId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log(`[useClientSets] Fetching client sets for company ${companyId}`);

      // Build query based on role
      const isAdmin = ['super_admin', 'administrator', 'office_admin'].includes(userRole);

      let clientSetsQuery = query(
        collection(db, `companies/${companyId}/client_sets`),
        orderBy('updated_at', 'desc')
      );

      // Brokers can only see their own client sets
      if (!isAdmin && userId) {
        clientSetsQuery = query(
          collection(db, `companies/${companyId}/client_sets`),
          where('created_by', '==', userId),
          orderBy('updated_at', 'desc')
        );
      }

      const snapshot = await getDocs(clientSetsQuery);
      console.log(`[useClientSets] Found ${snapshot.size} client sets`);

      const sets = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          clientSetName: data.client_set_name,
          customerNames: data.customer_names || [],
          createdBy: data.created_by,
          createdAt: data.created_at?.toDate() || new Date(),
          updatedAt: data.updated_at?.toDate() || new Date(),
          loanCount: Number(data.loan_count) || 0,
          totalValueCents: Number(data.total_value_cents) || 0,
        };
      });

      setClientSets(sets);
    } catch (err) {
      console.error('[useClientSets] Error fetching client sets:', err);
      setError(err instanceof Error ? err.message : 'Failed to load client sets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClientSets();
  }, [companyId, userId, userRole]);

  return {
    clientSets,
    loading,
    error,
    refetch: fetchClientSets,
  };
}
